/**
 * PlaceholderSuggestion TipTap Extension
 *
 * Requirements Summary:
 * - Typing "[" in the editor triggers an autocomplete dropdown
 * - Dropdown filters as the user types (e.g. "[Gym" shows Gym Name, Gym Phone, etc.)
 * - Selecting an item inserts the full placeholder code and closes the dropdown
 * - Uses tippy.js for positioning the popup near the cursor
 *
 * Related files:
 * - /utils/policies/policyPlaceholders.ts - AVAILABLE_POLICY_PLACEHOLDERS
 * - /components/form/fields/RichTextEditor.tsx - Editor that registers this extension
 */

import { ReactRenderer } from '@tiptap/react';
import { Extension } from '@tiptap/react';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PlaceholderItem {
  code: string;
  desc: string;
}

interface SuggestionListProps {
  items: PlaceholderItem[];
  command: (item: PlaceholderItem) => void;
}

export interface SuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dropdown Component
// ─────────────────────────────────────────────────────────────────────────────

const SuggestionList = forwardRef<SuggestionListRef, SuggestionListProps>(
  function SuggestionList({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    // Reset selection when items change
    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // Scroll selected item into view
    useEffect(() => {
      const container = containerRef.current;
      const selected = container?.children[selectedIndex] as HTMLElement | undefined;
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }, [selectedIndex]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item) command(item);
      },
      [items, command],
    );

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 p-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">No matching placeholders</p>
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className="rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 overflow-y-auto max-h-64 min-w-[260px]"
      >
        {items.map((item, index) => (
          <button
            key={item.code}
            type="button"
            onClick={() => selectItem(index)}
            className={`
              w-full text-left px-3 py-2 flex flex-col gap-0.5 transition-colors
              ${index === selectedIndex
                ? 'bg-violet-50 dark:bg-violet-900/30'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
              }
            `}
          >
            <span className="text-sm font-mono font-medium text-violet-700 dark:text-violet-300">
              {item.code}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</span>
          </button>
        ))}
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Reusable render plumbing (tippy popup + ReactRenderer list)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the @tiptap/suggestion `render` implementation for a dropdown list
 * component (any component taking `{ items, command }` and exposing
 * onKeyDown via ref). Shared by the policy placeholder editor and the email
 * template variable menu so the tippy/ReactRenderer machinery lives once.
 */
export function createSuggestionRenderer(
  ListComponent: React.ComponentType<any>,
): () => {
  onStart: (props: any) => void;
  onUpdate: (props: any) => void;
  onKeyDown: (props: any) => boolean;
  onExit: () => void;
} {
  return () => {
    let component: ReactRenderer<SuggestionListRef> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: any) => {
        component = new ReactRenderer(ListComponent, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          maxWidth: 'none',
        });
      },

      onUpdate(props: any) {
        component?.updateProps(props);
        if (popup && props.clientRect) {
          popup[0]?.setProps({
            getReferenceClientRect: props.clientRect,
          });
        }
      },

      onKeyDown(props: any) {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }
        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit() {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Extension Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates the PlaceholderSuggestion extension configured with a given list
 * of placeholders. Call this in the component that sets up the editor.
 */
export function createPlaceholderSuggestion(
  placeholders: PlaceholderItem[],
): Extension {
  return Extension.create({
    name: 'placeholderSuggestion',

    addOptions() {
      return {
        suggestion: {
          char: '[',
          // Allow matching everything after "[" until we close or hit 30 chars
          allowSpaces: true,
          // Items: filter the placeholder list by the query typed after "["
          items: ({ query }: { query: string }) => {
            const q = query.toLowerCase().replace(/]$/, '');
            if (!q) return placeholders;
            return placeholders.filter(
              (p) =>
                p.code.toLowerCase().includes(q) ||
                p.desc.toLowerCase().includes(q),
            );
          },
          // When user selects an item, replace the trigger + query with the placeholder code
          command: ({
            editor,
            range,
            props,
          }: {
            editor: any;
            range: any;
            props: PlaceholderItem;
          }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent(props.code)
              .run();
          },
          render: createSuggestionRenderer(SuggestionList),
        } satisfies Partial<SuggestionOptions>,
      };
    },

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          ...this.options.suggestion,
        }),
      ];
    },
  });
}
